import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Scale, HeartPulse, Landmark, Zap, Fuel, ShieldCheck, Eye, FileCheck2, Layers,
  Award, ScrollText, KeyRound, Lock, Server, Check,
} from "lucide-react";
import { Reveal, SectionHeading, Eyebrow } from "../components/common/Section";
import PageHero from "../components/common/PageHero";
import { Button } from "../components/ui/button";
import DeploymentExplorer from "../components/defence/DeploymentExplorer";

const SECTORS = [
  { id: "fs", icon: Landmark, t: "Financial services", d: "Regulated conversations, Consumer Duty evidence and QA at scale.", to: "/financial-services", focus: ["Customer-facing AI", "Call monitoring / QA", "Vulnerable customers", "Employee AI", "Reviewable evidence"], risk: "An AI assistant drifts toward advice beyond what the firm is authorised to give." },
  { id: "health", icon: HeartPulse, t: "Healthcare & life sciences", d: "Sensitive-data handling and human oversight around clinical and administrative AI.", focus: ["Sensitive-data protection", "Human oversight", "Output inspection", "Access controls", "Audit records"], risk: "An assistant surfaces sensitive personal information outside an authorised context." },
  { id: "legal", icon: Scale, t: "Legal & professional services", d: "Confidentiality, privilege and defensible records around AI assistance.", focus: ["Confidentiality controls", "Document handling", "Output review", "Evidence trail", "Role-based access"], risk: "Privileged or confidential material is entered into an unmanaged AI tool." },
  { id: "energy", icon: Zap, t: "Energy & utilities", d: "Policy enforcement and evidence for operational and customer AI.", focus: ["Policy enforcement", "Customer AI", "Operational oversight", "Incident review", "Management information"], risk: "A customer-facing assistant produces an unsupported or unsafe recommendation." },
  { id: "cni", icon: Fuel, t: "Critical national infrastructure", d: "Independent control and auditability around AI in sensitive environments.", focus: ["Independent control", "Action governance", "Human authorisation", "Deployment control", "Auditability"], risk: "An agent proposes an action outside its authorised scope." },
  { id: "gov", icon: Landmark, t: "Government & public sector", d: "Responsible AI adoption with accountability and reviewable evidence.", focus: ["Responsible AI", "Human accountability", "Policy enforcement", "Evidence", "Deployment options"], risk: "AI-assisted work needs a clear, accountable human decision point." },
];

const PILLARS = [
  { icon: ShieldCheck, t: "Policy enforcement", d: "Apply organisation-defined policy to every designated interaction in real time." },
  { icon: Eye, t: "Human oversight", d: "Escalation and final decisions stay with your accountable people." },
  { icon: FileCheck2, t: "Reviewable evidence", d: "Each handled interaction leaves the event, policy, action and decision." },
  { icon: Layers, t: "Model-agnostic", d: "One consistent control layer across a multi-vendor AI estate." },
];

function SectorSelector() {
  const [active, setActive] = useState(SECTORS[0].id);
  const cur = SECTORS.find((s) => s.id === active);
  const Icon = cur.icon;
  return (
    <div className="grid lg:grid-cols-[320px_1fr] gap-4" data-testid="regulated-sector-selector">
      <div className="flex flex-col gap-1.5">
        {SECTORS.map((s) => {
          const SIcon = s.icon; const on = s.id === active;
          return (
            <button key={s.id} onClick={() => setActive(s.id)} data-testid={`regulated-tab-${s.id}`}
              className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors ${on ? "border-primary/40 bg-primary/[0.07]" : "border-white/10 bg-navy-900/60 hover:border-white/20"}`}>
              <span className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${on ? "bg-primary/15 text-primary" : "bg-white/[0.04] text-muted-foreground"}`}><SIcon className="h-5 w-5" /></span>
              <span className={`text-sm font-medium ${on ? "text-foreground" : "text-muted-foreground"}`}>{s.t}</span>
            </button>
          );
        })}
      </div>
      <div className="rounded-lg border border-white/10 bg-navy-900/60 p-6 sm:p-8">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary/12 text-primary"><Icon className="h-5 w-5" /></span>
          <h3 className="text-xl font-bold text-foreground">{cur.t}</h3>
        </div>
        <p className="mt-4 text-base text-muted-foreground leading-relaxed">{cur.d}</p>
        <div className="mt-6 grid sm:grid-cols-2 gap-2.5">
          {cur.focus.map((f) => (
            <div key={f} className="flex items-center gap-2.5 rounded-lg border border-white/10 bg-navy-800/50 px-3.5 py-2.5 text-sm text-foreground/90"><Check className="h-4 w-4 text-primary" /> {f}</div>
          ))}
        </div>
        <p className="mt-5 rounded-lg border border-signal-amber/25 bg-signal-amber/[0.04] px-4 py-3 text-sm text-signal-amber/90">Example risk: {cur.risk}</p>
        {cur.to && <Link to={cur.to} className="mt-5 inline-flex items-center gap-1.5 text-sm text-primary" data-testid={`regulated-link-${cur.id}`}>Explore the {cur.t} vertical <ArrowRight className="h-4 w-4" /></Link>}
      </div>
    </div>
  );
}

export default function RegulatedIndustries() {
  useEffect(() => { document.title = "Regulated Industries | norse3"; }, []);
  return (
    <div>
      <PageHero
        eyebrow="Regulated Industries"
        title="Independent AI governance for regulated sectors."
        subtitle="Wherever obligations, sensitive data and accountability meet AI, norse3 provides a control and evidence layer that applies your policy, keeps humans in control and produces a reviewable record."
      />

      {/* THE PATTERN */}
      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal><SectionHeading eyebrow="The pattern" title="The same control lifecycle, applied to your obligations." subtitle="Regulated sectors differ in the rules, but the governance pattern is consistent." /></Reveal>
          <Reveal className="mt-8" delay={80}>
            <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
              {["Identify risk", "Apply your policy", "Intervene", "Keep a human accountable", "Evidence it"].map((s, i, arr) => (
                <div key={s} className="relative rounded-xl border border-white/10 bg-navy-900/60 p-5">
                  <span className="font-mono text-xs text-primary/60">0{i + 1}</span>
                  <p className="mt-2 text-sm font-semibold text-foreground">{s}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* SECTORS */}
      <section className="border-t border-white/10 py-16 lg:py-20 bg-navy-950/40">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal><SectionHeading eyebrow="Sectors" title="Built for the obligations you carry." subtitle="Our primary focus is UK financial services, with the same control layer applying across regulated sectors." /></Reveal>
          <Reveal className="mt-10" delay={80}><SectorSelector /></Reveal>
        </div>
      </section>

      {/* PILLARS */}
      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal><SectionHeading eyebrow="What you get" title="The same control layer, applied to your policy." /></Reveal>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {PILLARS.map((x, i) => {
              const Icon = x.icon;
              return (
                <Reveal key={x.t} delay={i * 60}>
                  <div className="h-full rounded-lg border border-white/10 bg-navy-900/60 p-6">
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary/12 text-primary"><Icon className="h-5 w-5" /></span>
                    <h3 className="mt-4 text-base font-semibold text-foreground">{x.t}</h3>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{x.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* DEPLOYMENT */}
      <section className="border-t border-white/10 py-16 lg:py-20 bg-navy-950/40">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal><SectionHeading eyebrow="Deployment" title="Deploy according to your sector's sensitivity." /></Reveal>
          <Reveal className="mt-10" delay={80}><DeploymentExplorer /></Reveal>
        </div>
      </section>

      {/* ASSURANCE */}
      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal><SectionHeading eyebrow="Assurance" title="Built for organisations where evidence matters." subtitle="Showing only what is verified today." /></Reveal>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { icon: Award, t: "ISO 9001", d: "Quality management — certified.", v: true },
              { icon: Award, t: "ISO 42001", d: "AI management system — certified.", v: true },
              { icon: ScrollText, t: "SOC 2", d: "Roadmap / readiness in progress — not yet certified.", v: false },
              { icon: KeyRound, t: "Configurable access", d: "Role-based access to the console and records." },
              { icon: Lock, t: "Configurable retention", d: "Retention windows configured to your policy." },
              { icon: Server, t: "Deployment flexibility", d: "Hosted, private cloud or customer-controlled." },
            ].map((x) => {
              const Icon = x.icon;
              return (
                <div key={x.t} className="h-full rounded-xl border border-white/10 bg-navy-900/60 p-6">
                  <div className="flex items-center justify-between">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/12 text-primary"><Icon className="h-5 w-5" /></span>
                    {x.v === true && <span className="rounded-full border border-signal-green/40 bg-signal-green/[0.06] px-2 py-0.5 text-[0.6rem] font-semibold text-signal-green">VERIFIED</span>}
                    {x.v === false && <span className="rounded-full border border-signal-amber/40 bg-signal-amber/[0.06] px-2 py-0.5 text-[0.6rem] font-semibold text-signal-amber">ROADMAP</span>}
                  </div>
                  <h3 className="mt-4 text-base font-semibold text-foreground">{x.t}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{x.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-white/10 py-16 lg:py-20 bg-navy-950/40">
        <div className="mx-auto max-w-4xl px-5 lg:px-8 text-center">
          <Reveal>
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground text-balance">See where norse3 fits in your sector.</h2>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90"><Link to="/book-a-demo">Book a walkthrough <ArrowRight className="ml-1.5 h-4 w-4" /></Link></Button>
              <Button asChild size="lg" variant="outline" className="border-white/15 bg-transparent hover:bg-white/5 text-foreground"><Link to="/trust-centre">Visit Trust Centre</Link></Button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
